'use client';
import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Search, X, Loader2, LifeBuoy, ChevronRight, FileText } from 'lucide-react';
import { HelpArticle, groupByCategory, helpService } from '@/lib/help/helpService';

function buildSnippet(body: string, query: string): string {
  const flat = (body ?? '').replace(/[#*_`>\[\]()-]/g, ' ').replace(/\s+/g, ' ').trim();
  const idx = flat.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return flat.slice(0, 120);
  const start = Math.max(0, idx - 40);
  const end = Math.min(flat.length, idx + query.length + 80);
  return `${start > 0 ? '…' : ''}${flat.slice(start, end)}${end < flat.length ? '…' : ''}`;
}

export default function HelpCentre() {
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await helpService.listPublished();
        if (!cancelled) setArticles(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Unable to load help articles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const term = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!term) return articles;
    return articles.filter(
      (a) => a.title.toLowerCase().includes(term) || a.bodyMarkdown.toLowerCase().includes(term),
    );
  }, [articles, term]);

  const categories = useMemo(() => groupByCategory(filtered), [filtered]);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <div className="card-surface p-5 md:p-8">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent">
            <LifeBuoy size={20} />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-card-foreground md:text-xl">How can we help?</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">Search the guides below or browse by topic.</p>
          </div>
        </div>
        <div className="relative mt-5">
          <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help articles…"
            aria-label="Search help articles"
            className="w-full rounded-lg border border-border bg-background py-3 pl-10 pr-10 text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 md:text-sm"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {!loading && !error && term && (
          <p className="mt-3 text-xs text-muted-foreground" aria-live="polite">
            {filtered.length} {filtered.length === 1 ? 'article' : 'articles'} found for “{query.trim()}”
          </p>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 size={18} className="animate-spin" /> Loading help articles…
        </div>
      )}

      {!loading && error && (
        <div className="card-surface p-8 text-center">
          <p className="text-base font-semibold text-card-foreground">Couldn’t load the Help Centre</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
      )}

      {!loading && !error && categories.length === 0 && (
        <div className="card-surface p-8 text-center">
          <p className="text-base font-semibold text-card-foreground">
            {term ? 'No articles match your search' : 'No help articles yet'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {term ? 'Try different keywords or clear the search.' : 'Please check back soon.'}
          </p>
        </div>
      )}

      {!loading && !error && categories.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          {categories.map((cat) => (
            <section key={cat.name} className="card-surface flex flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-card-foreground">{cat.name}</h3>
                <span className="tabular rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                  {cat.articles.length}
                </span>
              </div>
              <ul className="mt-3 divide-y divide-border">
                {cat.articles.map((a) => (
                  <li key={a.id}>
                    <Link
                      href={`/help/${a.id}`}
                      className="group flex items-start gap-2.5 py-3 text-sm text-foreground transition-colors duration-150 hover:text-primary"
                    >
                      <FileText size={16} className="mt-0.5 flex-shrink-0 text-accent" />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{a.title}</span>
                        {term && (
                          <span className="mt-0.5 block text-xs text-muted-foreground">{buildSnippet(a.bodyMarkdown, term)}</span>
                        )}
                      </span>
                      <ChevronRight size={16} className="mt-0.5 flex-shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
